/**
 * EmailConfirmedPage Component
 * Success page shown after email confirmation
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../../../shared/contexts/ToastContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';

export const EmailConfirmedPage: React.FC = () => {
  const navigate = useNavigate();
  const { isLoading } = useAuth();
  const { showSuccess } = useToast();
  
  // Show success message only (no auto-redirect)
  useEffect(() => {
    showSuccess('Email berhasil dikonfirmasi! Selamat datang di Canvango. 🎉');
    
    // Clean up URL parameters and mark as processed
    window.history.replaceState(null, '', window.location.pathname);
    sessionStorage.setItem('email_confirmation_processed', 'true');
    
    // Clean up any confirmation flags after showing the page
    setTimeout(() => {
      sessionStorage.removeItem('email_confirmation_notified');
    }, 1000);
  }, [showSuccess]);

  const handleManualRedirect = () => {
    navigate('/dashboard', { replace: true });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <FontAwesomeIcon 
            icon={faSpinner} 
            className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" 
          />
          <p className="text-sm text-gray-600">Memverifikasi email...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-3xl shadow-lg p-6 text-center">
          {/* Compact Success Icon */}
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FontAwesomeIcon icon={faCheckCircle} className="w-8 h-8 text-green-600" />
          </div>
          
          {/* Minimalist Success Message */}
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Email Berhasil Dikonfirmasi!
          </h1>
          
          <p className="text-sm text-gray-600 mb-6">
            Akun Anda telah aktif dan siap digunakan.
          </p>
          
          {/* Streamlined Navigation */}
          <button
            onClick={handleManualRedirect}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-4 rounded-xl font-medium transition-colors mb-3"
          >
            Lanjut ke Dashboard
          </button>

          {/* Bonus Member Baru CTA */}
          <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl mb-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                <img 
                  src="https://res.cloudinary.com/dubmxw6kl/image/upload/v1765420357/Desain_tanpa_judul_lx8vrv.jpg" 
                  alt="Bonus Icon"
                  className="w-full h-full object-cover"
                />
              </div>
              <p className="text-sm font-semibold text-green-800">
                Bonus Member Baru Tersedia!
              </p>
            </div>
            <p className="text-xs text-green-700 mb-2">
              Dapatkan hadiah spesial untuk member baru. Hubungi admin sekarang untuk mengklaim bonus Anda!
            </p>
            <a
              href="https://wa.me/6281234567890?text=Halo%20admin,%20saya%20member%20baru%20dan%20ingin%20mengklaim%20bonus%20member%20baru.%20Terima%20kasih!"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg font-medium transition-colors"
            >
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
              </svg>
              Hubungi Admin
            </a>
          </div>

          {/* Compact Welcome Message */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-xs text-blue-800">
              🎉 <strong>Selamat datang di Canvango!</strong> Semua fitur marketplace siap digunakan.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Add default export for lazy loading compatibility
export default EmailConfirmedPage;